import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/lib/seo";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import { UploadSimple, MusicNoteSimple, Image as ImageIcon, Plus, X } from "@phosphor-icons/react";

const GENRES = ["Hip-Hop", "R&B", "Trap", "Drill", "Afrobeats", "Pop", "Electronic", "Rock", "Alt", "Indie", "Jazz", "House", "Techno", "Latin", "Other"];
const PLATFORMS = ["Spotify", "Apple Music", "TIDAL", "Amazon Music", "YouTube Music", "Deezer", "Pandora", "SoundCloud", "Audiomack", "TikTok", "Instagram"];
const TRACK_LIMITS = { asylum_starter: 1, asylum_pro: 8, asylum_label: 999 };
const TYPES = [
  { id: "single", label: "Single", hint: "1 track" },
  { id: "ep", label: "EP", hint: "2–8 tracks" },
  { id: "album", label: "Album", hint: "9+ tracks" },
];

const inputCls = "w-full bg-transparent border border-zinc-800 px-4 py-3 text-sm focus:border-red-600 focus:outline-none";
const newTrack = () => ({ key: crypto.randomUUID(), title: "", file: null });

export default function Upload() {
  const nav = useNavigate();
  const { user } = useAuth();
  const limit = TRACK_LIMITS[user?.subscription_tier] ?? 1;
  const [form, setForm] = useState({
    title: "", artist_name: user?.display_name || "", genre: "Hip-Hop",
    release_date: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
    explicit: false, isrc: "", writers: "", spotify_url: "", apple_url: "",
  });
  const [releaseType, setReleaseType] = useState("single");
  const [tracks, setTracks] = useState([newTrack()]);
  const [cover, setCover] = useState(null);
  const [platforms, setPlatforms] = useState(PLATFORMS);
  const [busy, setBusy] = useState(false);
  const [roster, setRoster] = useState([]);
  const [splits, setSplits] = useState([]);
  const isLabel = user?.subscription_tier === "asylum_label" && !user?.label_id;
  const labelShare = 100 - splits.reduce((s, x) => s + (Number(x.percent) || 0), 0);
  const leadDays = Math.round((new Date(`${form.release_date}T00:00:00`) - new Date().setHours(0, 0, 0, 0)) / 86400000);

  useEffect(() => { if (isLabel) api.get("/label/artists").then(({ data }) => setRoster(data.artists)).catch(() => {}); }, [isLabel]);

  const togglePlatform = (p) => setPlatforms((cur) => cur.includes(p) ? cur.filter(x => x !== p) : [...cur, p]);
  const setTrack = (i, patch) => setTracks((cur) => cur.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const addTrack = () => {
    if (tracks.length >= limit) { toast.error(limit === 1 ? "Starter plan is singles only. Upgrade to Pro for EPs." : `Your plan allows up to ${limit} tracks.`); return; }
    setTracks((cur) => [...cur, newTrack()]);
  };
  const removeTrack = (i) => setTracks((cur) => cur.filter((_, idx) => idx !== i));
  const pickType = (t) => {
    setReleaseType(t);
    if (t === "single") setTracks((cur) => cur.slice(0, 1));
    else if (tracks.length < 2) setTracks((cur) => [...cur, newTrack()]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (tracks.some(t => !t.file)) { toast.error("Every track needs an audio file"); return; }
    if (tracks.length > 1 && tracks.some(t => !t.title.trim())) { toast.error("Every track needs a title"); return; }
    if (!cover) { toast.error("Cover art required"); return; }
    if (platforms.length === 0) { toast.error("Select at least one platform"); return; }
    if (labelShare < 0) { toast.error("Splits exceed 100%"); return; }

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (splits.length) fd.append("splits", JSON.stringify(splits));
    fd.append("release_type", tracks.length > 1 ? releaseType : "single");
    fd.append("platforms", platforms.join(","));
    fd.append("track_titles", tracks.map(t => t.title.trim()).join("|"));
    tracks.forEach(t => fd.append("audio", t.file));
    fd.append("cover", cover);

    setBusy(true);
    try {
      await api.post("/releases", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Release submitted for review");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Seo title="Upload Release" path="/upload" noindex description="Upload a release." />
      <Navbar />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
        <div className="text-[10px] tracking-[0.3em] uppercase text-red-500 mb-3">— New Release</div>
        <h1 className="font-display text-4xl lg:text-6xl uppercase tracking-tighter mb-2">Upload<span className="text-red-600">.</span></h1>
        <p className="text-zinc-500 text-sm mb-10">Singles, EPs, or full albums in one flow. Review typically takes 24-48h on Pro/Label, 5-7d on Starter.</p>

        <form onSubmit={submit} data-testid="upload-form" className="grid lg:grid-cols-3 gap-0 border border-zinc-800">
          <div className="lg:col-span-2 p-8 border-r border-zinc-800 space-y-6">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-3">Release Type</div>
              <div className="grid grid-cols-3 gap-0 border border-zinc-800" data-testid="release-type-picker">
                {TYPES.map(t => {
                  const locked = t.id !== "single" && limit === 1;
                  return (
                    <button type="button" key={t.id} disabled={locked} onClick={() => pickType(t.id)} data-testid={`release-type-${t.id}`}
                      className={`p-4 text-left border-r border-zinc-800 last:border-r-0 transition-colors ${releaseType === t.id ? "bg-red-600 text-white" : "hover:bg-zinc-900"} disabled:opacity-30 disabled:cursor-not-allowed`}>
                      <div className="font-display text-lg uppercase tracking-tight">{t.label}</div>
                      <div className={`text-[10px] tracking-widest uppercase ${releaseType === t.id ? "text-red-100" : "text-zinc-500"}`}>{locked ? "Pro+" : t.hint}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">{releaseType === "single" ? "Track Title" : `${releaseType === "ep" ? "EP" : "Album"} Title`}</div>
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="upload-title" className={inputCls} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Artist Name</div>
                <input required value={form.artist_name} onChange={(e) => setForm({ ...form, artist_name: e.target.value })} data-testid="upload-artist" className={inputCls} />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Genre</div>
                <select value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} data-testid="upload-genre" className={inputCls}>
                  {GENRES.map(g => <option key={g} value={g} className="bg-zinc-900">{g}</option>)}
                </select>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Release Date</div>
                <input type="date" required value={form.release_date} onChange={(e) => setForm({ ...form, release_date: e.target.value })} data-testid="upload-date" className={inputCls} />
                {leadDays < 14 && <p className="text-[10px] text-yellow-500 mt-2 leading-relaxed" data-testid="lead-time-warning">Heads up: stores need lead time. Spotify/Apple take 2–5 business days after Too Lost QC (1–3 days). Pick a date ≥ 2 weeks out to hit your release day.</p>}
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">ISRC (optional)</div>
                <input value={form.isrc} onChange={(e) => setForm({ ...form, isrc: e.target.value })} placeholder="AA-XXX-YY-NNNNN" data-testid="upload-isrc" className={inputCls} />
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Songwriters (legal names, comma-separated)</div>
              <input value={form.writers} onChange={(e) => setForm({ ...form, writers: e.target.value })} placeholder="e.g. Whitney Moses, Jane Doe" data-testid="upload-writers" className={inputCls} />
              <p className="text-[10px] text-zinc-600 mt-1">Required by stores for publishing credits — first and last name for each writer. Defaults to the artist name if left blank.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Spotify Link (for pre-save page)</div>
                <input value={form.spotify_url} onChange={(e) => setForm({ ...form, spotify_url: e.target.value })} placeholder="https://open.spotify.com/..." data-testid="upload-spotify-url" className={inputCls} />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Apple Music Link (for pre-save page)</div>
                <input value={form.apple_url} onChange={(e) => setForm({ ...form, apple_url: e.target.value })} placeholder="https://music.apple.com/..." data-testid="upload-apple-url" className={inputCls} />
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.explicit} onChange={(e) => setForm({ ...form, explicit: e.target.checked })} data-testid="upload-explicit" className="w-4 h-4 accent-red-600" />
              <span className="text-sm">Contains explicit content</span>
            </label>

            {isLabel && (
              <div data-testid="splits-section">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Royalty Splits <span className="text-zinc-600">· Label</span></div>
                  <span className={`text-[10px] font-mono ${labelShare < 0 ? "text-red-500" : "text-zinc-400"}`} data-testid="label-share">You keep {labelShare}%</span>
                </div>
                {roster.length === 0 ? (
                  <p className="text-xs text-zinc-600">Add artists to your roster on the dashboard to assign splits.</p>
                ) : (
                  <div className="space-y-2">
                    {roster.map(a => {
                      const s = splits.find(x => x.user_id === a.id);
                      return (
                        <div key={a.id} className={`flex items-center gap-3 border px-3 py-2 transition-colors ${s ? "border-red-600/50" : "border-zinc-800"}`} data-testid={`split-row-${a.id}`}>
                          <input type="checkbox" checked={!!s} data-testid={`split-toggle-${a.id}`} className="w-4 h-4 accent-red-600"
                            onChange={(e) => setSplits(cur => e.target.checked ? [...cur, { user_id: a.id, display_name: a.display_name, percent: 50 }] : cur.filter(x => x.user_id !== a.id))} />
                          <span className="flex-1 text-sm truncate">{a.display_name}</span>
                          {s && (
                            <div className="flex items-center gap-1">
                              <input type="number" min="1" max="100" value={s.percent} data-testid={`split-percent-${a.id}`}
                                onChange={(e) => setSplits(cur => cur.map(x => x.user_id === a.id ? { ...x, percent: Number(e.target.value) } : x))}
                                className="w-16 bg-transparent border border-zinc-800 px-2 py-1 text-sm text-right focus:border-red-600 focus:outline-none" />
                              <span className="text-xs text-zinc-500">%</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-3">Distribute to</div>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map(p => (
                  <button type="button" key={p} onClick={() => togglePlatform(p)} data-testid={`platform-${p.toLowerCase().replace(/[^a-z]/g,'-')}`}
                    className={`text-xs tracking-[0.15em] uppercase px-3 py-2 border transition-colors ${platforms.includes(p) ? "border-red-600 bg-red-600/10 text-white" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="p-8 space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Tracks <span className="text-zinc-600">({tracks.length}{limit < 999 ? `/${limit}` : ""})</span></div>
                {releaseType !== "single" && (
                  <button type="button" onClick={addTrack} data-testid="add-track-btn" className="text-[10px] tracking-[0.2em] uppercase text-red-500 hover:text-white inline-flex items-center gap-1">
                    <Plus size={12} weight="bold" /> Add Track
                  </button>
                )}
              </div>
              <div className="space-y-2" data-testid="track-list">
                {tracks.map((t, i) => (
                  <div key={t.key} className="border border-zinc-800 p-3" data-testid={`track-row-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-display text-sm text-zinc-500 w-6">{String(i + 1).padStart(2, "0")}</span>
                      {releaseType !== "single" ? (
                        <input value={t.title} onChange={(e) => setTrack(i, { title: e.target.value })} placeholder="Track title" data-testid={`track-title-${i}`}
                          className="flex-1 bg-transparent border-b border-zinc-800 px-1 py-1 text-sm focus:border-red-600 focus:outline-none" />
                      ) : <span className="flex-1 text-sm text-zinc-400">Audio file</span>}
                      {tracks.length > 1 && (
                        <button type="button" onClick={() => removeTrack(i)} data-testid={`track-remove-${i}`} className="text-zinc-600 hover:text-red-500"><X size={14} /></button>
                      )}
                    </div>
                    <label className={`flex items-center gap-2 cursor-pointer text-xs px-2 py-2 border border-dashed transition-colors ${t.file ? "border-red-600/50 text-white" : "border-zinc-800 text-zinc-500 hover:border-red-600"}`}>
                      <MusicNoteSimple size={14} weight="duotone" className={t.file ? "text-red-500" : ""} />
                      <span className="truncate">{t.file ? t.file.name : "Select MP3 / WAV / FLAC"}</span>
                      <input type="file" accept="audio/*,.mp3,.wav,.flac,.m4a" className="hidden" data-testid={`track-file-${i}`}
                        onChange={(e) => setTrack(i, { file: e.target.files?.[0] || null })} />
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <FileDrop icon={ImageIcon} label="Cover Art" hint="JPG or PNG, exactly square, min 1400×1400 (3000×3000 recommended)" accept="image/*" file={cover} setFile={setCover} testid="upload-cover" />

            <button type="submit" disabled={busy} data-testid="upload-submit"
              className="w-full bg-red-600 text-white py-4 text-xs tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2 btn-brutal btn-brutal-red disabled:opacity-50">
              <UploadSimple size={14} weight="bold" /> {busy ? "Uploading..." : "Submit for Review"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function FileDrop({ icon: Icon, label, hint, accept, file, setFile, testid }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">{label}</div>
      <label className="border border-dashed border-zinc-800 p-6 flex flex-col items-center justify-center text-center cursor-pointer hover:border-red-600 transition-colors block">
        <Icon size={28} weight="duotone" className={file ? "text-red-500 mb-2" : "text-zinc-600 mb-2"} />
        <div className="text-sm mb-1">{file ? file.name : "Click to select file"}</div>
        <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-600">{hint}</div>
        <input type="file" accept={accept} onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid={testid} />
      </label>
    </div>
  );
}
